/**
 * Utilitários compartilhados para geração de PDFs
 * Garante cabeçalho, logo e estilo consistentes em todos os documentos.
 */

// Cache do logo para evitar fetch repetido
let _logoBase64Cache = null

export async function loadLogo() {
  if (_logoBase64Cache) return _logoBase64Cache

  const possiblePaths = [
    '/logo.png',
    './logo.png',
    `${window.location.origin}/logo.png`,
    'logo.png'
  ]

  for (const logoUrl of possiblePaths) {
    try {
      const response = await fetch(logoUrl)
      if (response.ok) {
        const blob = await response.blob()
        const reader = new FileReader()
        const result = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(blob)
        })
        if (result) {
          _logoBase64Cache = result
          return _logoBase64Cache
        }
      }
    } catch (e) {
      // Tenta próximo caminho
    }
  }

  return null
}

/**
 * Desenha o cabeçalho padrão em um documento jsPDF.
 * Layout paisagem (297x210) ou retrato (210x297).
 *
 * @param {object} doc - Instância jsPDF
 * @param {object} options
 * @param {string} options.titulo - Título principal (ex: "PEDIDO DE COMPRA")
 * @param {string} options.subtitulo - Subtítulo (ex: "Nº PED-001")
 * @param {string} [options.usuario] - Nome do usuário que gerou
 * @param {string} [options.infoExtra] - Info adicional à direita
 * @param {boolean} [options.landscape=true] - Se paisagem
 */
export async function drawPDFHeader(doc, { titulo, subtitulo, usuario, infoExtra, landscape = false }) {
  // Tentar carregar logo
  const logoBase64 = await loadLogo()
  
  // Configurações baseadas no formato
  const pageWidth = landscape ? 297 : 210
  const pageHeight = landscape ? 210 : 297
  
  // Fundo azul escuro como na imagem de exemplo
  doc.setFillColor(0, 51, 102) // Azul escuro profissional
  doc.rect(0, 0, pageWidth, 65, 'F')
  
  // Logo no canto superior esquerdo
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', 14, 12, 45, 45)
    } catch (e) {
      console.warn('Erro ao adicionar logo:', e)
    }
  }
  
  // Título principal centralizado
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255) // Texto branco no cabeçalho azul
  doc.text(titulo, landscape ? 148 : 105, 25, { align: 'center' })
  
  // Subtítulo centralizado e melhorado
  doc.setFontSize(16)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(255, 255, 255) // Texto branco no cabeçalho azul
  doc.text(subtitulo, landscape ? 148 : 105, 40, { align: 'center' })

  // Informações à direita
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(255, 255, 255) // Texto branco no cabeçalho azul
  const rightX = pageWidth - 20
  doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, rightX, 20, { align: 'right' })
  if (infoExtra) doc.text(infoExtra, rightX, 30, { align: 'right' })
  if (usuario) doc.text(`Gerado por: ${usuario}`, rightX, 40, { align: 'right' })

  // Linha separadora após cabeçalho
  const headerHeight = 65
  doc.setDrawColor(255, 255, 255) // Linha branca para contrastar com azul escuro
  doc.setLineWidth(1)
  const lineEnd = pageWidth
  doc.line(0, headerHeight, lineEnd, headerHeight)
}

/**
 * Desenha célula de tabela com estilo padrão.
 */
export function drawTableCell(doc, x, y, width, height, text, align = 'left', isHeader = false) {
  // Validar parâmetros
  if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number' || typeof height !== 'number') {
    console.warn('Parâmetros inválidos para drawTableCell:', { x, y, width, height })
    return
  }
  
  // Desenhar retângulo com preenchimento e bordas
  if (isHeader) {
    doc.setFillColor(255, 140, 0) // Laranja do programa para cabeçalho
  } else {
    doc.setFillColor(255, 255, 255) // Branco para conteúdo
  }
  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.3)
  doc.rect(x, y, width, height, 'FD') // Preencher e desenhar bordas
  
  // Configurar cor do texto após desenhar o retângulo
  if (isHeader) {
    doc.setTextColor(0, 0, 0) // Texto preto no cabeçalho laranja
  } else {
    doc.setTextColor(0, 0, 0) // Texto preto no conteúdo
  }
  
  doc.setFont('helvetica', isHeader ? 'bold' : 'normal')
  doc.setFontSize(isHeader ? 10 : 9)
  
  const textX = align === 'center' ? x + width / 2 : x + 2
  const textY = y + height / 2 + 2
  doc.text(text, textX, textY, { align: align === 'center' ? 'center' : 'left' })
}

/**
 * Desenha linha de cabeçalho de tabela.
 */
export function drawTableHeader(doc, headers, y, colWidths, align = 'left') {
  let x = 14
  headers.forEach((header, idx) => {
    drawTableCell(doc, x, y, colWidths[idx], 10, header, align, true)
    x += colWidths[idx]
  })
  
  // Adicionar linhas verticais divisorias
  x = 14
  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.3)
  for (let i = 0; i < colWidths.length - 1; i++) {
    x += colWidths[i]
    doc.line(x, y, x, y + 10)
  }
}

/**
 * Desenha linha de dados de tabela.
 */
export function drawTableRow(doc, items, y, colWidths, align = 'left') {
  let x = 14
  items.forEach((item, idx) => {
    drawTableCell(doc, x, y, colWidths[idx], 8, item, align, false)
    x += colWidths[idx]
  })
  
  // Adicionar linhas verticais divisorias
  x = 14
  doc.setDrawColor(0, 0, 0)
  doc.setLineWidth(0.3)
  for (let i = 0; i < colWidths.length - 1; i++) {
    x += colWidths[i]
    doc.line(x, y, x, y + 8)
  }
}

/**
 * Verifica se precisa de nova página e desenha cabeçalho da tabela novamente.
 * Retorna a nova posição Y.
 */
export function checkNewPage(doc, y, maxY, headers, colWidths, align = 'center') {
  if (y > maxY) {
    doc.addPage()
    y = 20
    drawTableHeader(doc, headers, y, colWidths, align)
    y += 10
  }
  return y
}
